import React from 'react';
import { Link } from 'react-router-dom';
import { getBtnData } from '../../api/Api';
import Title from '../title';
import Loading from '../Loading';
import Input from '../Input';
import style from '../../assets/styles/SimpleSalesModule.module.scss';

const SimpleSalesModule = () => {
  const getTodayString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  // Estado dos inputs de data (não dispara filtro automaticamente ao alterar)
  const [form, setForm] = React.useState({
    startDate: getTodayString(),
    endDate: getTodayString(),
  });

  // Datas efetivamente aplicadas pelo botão "Filtrar"
  const [appliedDates, setAppliedDates] = React.useState({
    startDate: getTodayString(),
    endDate: getTodayString(),
  });

  const [dateError, setDateError] = React.useState('');
  const [rawDishesList, setRawDishesList] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // Formata moeda brasileira R$ 0,00
  const formatCurrency = (val) => {
    return Number(val || 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  };

  // Normaliza e parseia datas no formato 'DD/MM/YYYY - HH:mm:ss', 'DD/MM/YYYY HH:mm', etc.
  const parseItemDateTime = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    const cleanStr = dateStr.replace(' - ', ' ').trim();
    const [datePart, timePart] = cleanStr.split(' ');
    if (!datePart) return null;

    let day, month, year;
    if (datePart.includes('/')) {
      const parts = datePart.split('/');
      day = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10) - 1;
      year = parseInt(parts[2], 10);
      if (year < 100) year += 2000;
    } else if (datePart.includes('-')) {
      const parts = datePart.split('-');
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10) - 1;
      day = parseInt(parts[2], 10);
    } else {
      return null;
    }

    let hours = 0, minutes = 0, seconds = 0;
    if (timePart) {
      const timeParts = timePart.split(':');
      hours = parseInt(timeParts[0] || 0, 10);
      minutes = parseInt(timeParts[1] || 0, 10);
      seconds = parseInt(timeParts[2] || 0, 10);
    }

    const d = new Date(year, month, day, hours, minutes, seconds);
    return isNaN(d.getTime()) ? null : d;
  };

  // Formata data e hora para exibição na linha da tabela (ex: 29/10/2026 13:30)
  const formatDisplayDateTime = (dateObj, originalStr) => {
    if (!dateObj) return originalStr || '-';
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const yyyy = dateObj.getFullYear();
    const hh = String(dateObj.getHours()).padStart(2, '0');
    const min = String(dateObj.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  };

  // Busca todos os pedidos e desagrega item a item (linha individual para cada venda)
  React.useEffect(() => {
    let isMounted = true;
    const fetchRequests = async () => {
      setLoading(true);
      try {
        const requestData = await getBtnData('requests');
        if (!isMounted) return;

        const individualDishes = [];

        if (Array.isArray(requestData)) {
          requestData.forEach((order) => {
            if (!order || !Array.isArray(order.request)) return;

            order.request.forEach((item, itemIdx) => {
              if (!item) return;

              // Cada unidade de item vendido gera uma linha separada
              const quantity = Math.max(1, Number(item.amount || item.quantity || 1));
              const unitPrice = Number(
                item.finalPrice !== undefined
                  ? item.finalPrice
                  : item.price !== undefined
                  ? item.price
                  : 0
              );

              const rawDateStr = item.sentToKitchenTime || order.dateTime || '';
              const parsedDate = parseItemDateTime(rawDateStr);

              for (let q = 0; q < quantity; q++) {
                individualDishes.push({
                  id: `${order.id || 'ord'}-${item.id || itemIdx}-${q}`,
                  orderId: order.id,
                  name: item.name || 'Item sem nome',
                  dateTimeStr: rawDateStr,
                  dateTimeObj: parsedDate,
                  price: unitPrice,
                });
              }
            });
          });
        }

        // Ordena por data decrescente (mais recentes primeiro)
        individualDishes.sort((a, b) => {
          const timeA = a.dateTimeObj ? a.dateTimeObj.getTime() : 0;
          const timeB = b.dateTimeObj ? b.dateTimeObj.getTime() : 0;
          return timeB - timeA;
        });

        setRawDishesList(individualDishes);
      } catch (err) {
        console.error('Erro ao carregar pedidos para Vendas Simplificado:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchRequests();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  // Aplica o filtro de data somente quando o botão Filtrar é clicado
  const handleApplyFilter = () => {
    if (!form.startDate || !form.endDate) {
      setDateError('Por favor, selecione a data inicial e a data final.');
      return;
    }

    if (form.startDate > form.endDate) {
      setDateError('As datas são incompatíveis: a data de início não pode ser posterior à data de término.');
      return;
    }

    setDateError('');
    setAppliedDates({
      startDate: form.startDate,
      endDate: form.endDate,
    });
  };

  // Atalhos rápidos de data (preenche e já aplica)
  const handleSetQuickDate = (type) => {
    const today = new Date();
    const formatDate = (d) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    let start = '';
    let end = formatDate(today);

    if (type === 'today') {
      start = formatDate(today);
    } else if (type === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      start = formatDate(yesterday);
      end = formatDate(yesterday);
    } else if (type === 'last7') {
      const past7 = new Date(today);
      past7.setDate(today.getDate() - 6);
      start = formatDate(past7);
    } else if (type === 'month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      start = formatDate(firstDay);
    }

    setForm({ startDate: start, endDate: end });
    setDateError('');
    setAppliedDates({ startDate: start, endDate: end });
  };

  // Filtragem dos itens pelo período aplicado
  const filteredItems = React.useMemo(() => {
    if (dateError) return [];
    if (!appliedDates.startDate || !appliedDates.endDate) return rawDishesList;

    const [sYear, sMonth, sDay] = appliedDates.startDate.split('-').map(Number);
    const [eYear, eMonth, eDay] = appliedDates.endDate.split('-').map(Number);

    const start = new Date(sYear, sMonth - 1, sDay, 0, 0, 0, 0);
    const end = new Date(eYear, eMonth - 1, eDay, 23, 59, 59, 999);

    return rawDishesList.filter((item) => {
      if (!item.dateTimeObj) return true;
      return item.dateTimeObj >= start && item.dateTimeObj <= end;
    });
  }, [rawDishesList, appliedDates, dateError]);

  // Cálculos de totais
  const totals = React.useMemo(() => {
    const totalItems = filteredItems.length;
    const totalRevenue = filteredItems.reduce((acc, curr) => acc + (curr.price || 0), 0);
    return {
      totalItems,
      totalRevenue,
    };
  }, [filteredItems]);

  return (
    <div className={style.simpleSalesContainer}>
      {/* Botão de Sair do Módulo com seta para a esquerda */}
      <Link to="/admin/admin" className={style.btnBack} title="Sair do Módulo">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
      </Link>

      <Link to="/admin/admin" className={style.backLink}>
        <Title mainTitle="Vendas" />
      </Link>

      {/* Controles de Filtro de Data */}
      <div className={style.filterSection}>
        <div className={style.dateInputsRow}>
          <div className={style.dateInputWrapper}>
            <Input
              id="startDate"
              label="Data Inicial"
              type="date"
              value={form.startDate}
              onChange={handleChange}
            />
          </div>
          <div className={style.dateInputWrapper}>
            <Input
              id="endDate"
              label="Data Final"
              type="date"
              value={form.endDate}
              onChange={handleChange}
            />
          </div>
          <div className={style.filterBtnWrapper}>
            <button
              type="button"
              className={style.btnFilterSubmit}
              onClick={handleApplyFilter}
            >
              Filtrar
            </button>
          </div>
        </div>

        {/* Atalhos Rápidos para Mobile e Desktop */}
        <div className={style.quickFiltersRow}>
          <button
            type="button"
            className={style.quickBtn}
            onClick={() => handleSetQuickDate('today')}
          >
            Hoje
          </button>
          <button
            type="button"
            className={style.quickBtn}
            onClick={() => handleSetQuickDate('yesterday')}
          >
            Ontem
          </button>
          <button
            type="button"
            className={style.quickBtn}
            onClick={() => handleSetQuickDate('last7')}
          >
            Últimos 7 dias
          </button>
          <button
            type="button"
            className={style.quickBtn}
            onClick={() => handleSetQuickDate('month')}
          >
            Este Mês
          </button>
        </div>
      </div>

      {loading && <Loading />}

      {/* Tabela de Vendas Individuais */}
      <div className={style.tableWrapper}>
        <table className={style.salesTable}>
          <thead>
            <tr>
              <th className={style.thName}>Nome</th>
              <th className={style.thDate}>Data / Horário</th>
              <th className={style.thPrice}>Valor</th>
            </tr>
          </thead>
          <tbody>
            {dateError ? (
              <tr>
                <td colSpan="3" className={style.errorMessage}>
                  ⚠️ {dateError}
                </td>
              </tr>
            ) : filteredItems.length === 0 ? (
              <tr>
                <td colSpan="3" className={style.emptyMessage}>
                  {loading
                    ? 'Carregando vendas...'
                    : 'No período não há registro de vendas.'}
                </td>
              </tr>
            ) : (
              filteredItems.map((dish) => (
                <tr key={dish.id} className={style.dishRow}>
                  <td className={style.tdName}>{dish.name}</td>
                  <td className={style.tdDate}>
                    {formatDisplayDateTime(dish.dateTimeObj, dish.dateTimeStr)}
                  </td>
                  <td className={style.tdPrice}>{formatCurrency(dish.price)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Rodapé Fixo de Totais */}
      <div className={style.fixedTotalsFooter}>
        <div className={style.totalsContent}>
          <div className={style.footerColTotal}>
            <span className={style.footerLabel}>Total</span>
          </div>
          <div className={style.footerColCount}>
            <span className={style.itemsCountBadge}>
              {totals.totalItems} {totals.totalItems === 1 ? 'item vendido' : 'itens vendidos'}
            </span>
          </div>
          <div className={style.footerColRevenue}>
            <span className={style.totalRevenueBadge}>
              {formatCurrency(totals.totalRevenue)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimpleSalesModule;
